import log from 'loglevel'
import React from 'react'
import { Typography } from '@mui/material'
import { Confirm, useNotify, useTranslate } from 'react-admin'
import { includes, isNil, map, split } from 'lodash/fp'
import {
  doAccountsDownload,
  doCognitoDownload,
  doContactListDownload,
  doDownload,
  doOrganizationsDownload,
  doSummaryDownload
} from './functions'
import { type Partner } from '@mzc-watchover/adminapp-appsync'

log.setLevel(log.levels.DEBUG)

export const DownloadDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
  partner: Partial<Partner> | undefined
  permissions: string[]
}> = ({ open, setOpen, partner, permissions }) => {
  const translate = useTranslate()
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    setOpen(false)
    doDownload(
      partner,
      includes('editCriterion')(permissions),
      includes('changeContact')(permissions)
    )
      .then((_status: number | undefined) => {})
      .catch(log.error)
  }
  return (
    <Confirm
      isOpen={open}
      title={false}
      content={map((item: string) => (
        <Typography variant="body1" key={item}>
          {item}
          <br />
        </Typography>
      ))(split('\n')(translate('resources.download.dialog.content')))}
      onClose={onClose}
      onConfirm={onConfirm}
      cancel="resources.download.dialog.buttons.cancel"
      confirm="resources.download.dialog.buttons.confirm"
    />
  )
}
export const CognitoDownloadDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
}> = ({ open, setOpen }) => {
  const translate = useTranslate()
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    setOpen(false)
    doCognitoDownload()
      .then((_status: number | undefined) => {})
      .catch(log.error)
  }
  return (
    <Confirm
      isOpen={open}
      title={false}
      content={map((item: string) => (
        <Typography variant="body1" key={item}>
          {item}
          <br />
        </Typography>
      ))(split('\n')(translate('resources.cognito.download.dialog.content')))}
      onClose={onClose}
      onConfirm={onConfirm}
      cancel="resources.cognito.download.dialog.buttons.cancel"
      confirm="resources.cognito.download.dialog.buttons.confirm"
    />
  )
}
export const SummaryDownloadDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
  partner: string
  item: string
}> = ({ open, setOpen, partner, item }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    setOpen(false)
    doSummaryDownload(partner, item)
      .then((status: number | undefined) => {
        log.info({ status })
        if (isNil(status) || status !== 202) {
          notify('cannot download summary')
        }
      })
      .catch(log.error)
  }
  return (
    <Confirm
      isOpen={open}
      title={false}
      content={map((item: string) => (
        <Typography variant="body1" key={item}>
          {item}
          <br />
        </Typography>
      ))(split('\n')(translate('resources.summary.download.dialog.content')))}
      onClose={onClose}
      onConfirm={onConfirm}
      cancel="resources.summary.download.dialog.buttons.cancel"
      confirm="resources.summary.download.dialog.buttons.confirm"
    />
  )
}
export const OrganizationsDownloadDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
  partner: Partial<Partner> | undefined
  permissions: string[]
}> = ({ open, setOpen, partner }) => {
  const translate = useTranslate()
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    setOpen(false)
    doOrganizationsDownload(partner)
      .then((_status: number | undefined) => {})
      .catch(log.error)
  }
  return (
    <Confirm
      isOpen={open}
      title={false}
      content={map((item: string) => (
        <Typography variant="body1" key={item}>
          {item}
          <br />
        </Typography>
      ))(
        split('\n')(
          translate('resources.editOrganizations.download.dialog.content')
        )
      )}
      onClose={onClose}
      onConfirm={onConfirm}
      cancel="resources.editOrganizations.download.dialog.buttons.cancel"
      confirm="resources.editOrganizations.download.dialog.buttons.confirm"
    />
  )
}
export const AccountsDownloadDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
  partner: Partial<Partner> | undefined
  permissions: string[]
}> = ({ open, setOpen, partner }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    setOpen(false)
    doAccountsDownload(partner)
      .then((status: number | undefined) => {
        if (status === 404) {
          notify('resources.editAccounts.download.notFound')
        }
      })
      .catch(log.error)
  }
  return (
    <Confirm
      isOpen={open}
      title={false}
      content={map((item: string) => (
        <Typography variant="body1" key={item}>
          {item}
          <br />
        </Typography>
      ))(
        split('\n')(translate('resources.editAccounts.download.dialog.content'))
      )}
      onClose={onClose}
      onConfirm={onConfirm}
      cancel="resources.editAccounts.download.dialog.buttons.cancel"
      confirm="resources.editAccounts.download.dialog.buttons.confirm"
    />
  )
}
export const ContactListDownloadDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
  partner: Partial<Partner> | undefined
}> = ({ open, setOpen, partner }) => {
  const translate = useTranslate()
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    setOpen(false)
    doContactListDownload(partner)
      .then((_status: number | undefined) => {})
      .catch(log.error)
  }
  return (
    <Confirm
      isOpen={open}
      title={false}
      content={map((item: string) => (
        <Typography variant="body1" key={item}>
          {item}
          <br />
        </Typography>
      ))(
        split('\n')(
          translate('resources.contactListDownload.download.dialog.content')
        )
      )}
      onClose={onClose}
      onConfirm={onConfirm}
      cancel="resources.contactListDownload.download.dialog.buttons.cancel"
      confirm="resources.contactListDownload.download.dialog.buttons.confirm"
    />
  )
}
